import api, { type RequestOptions } from './api';

// ============================================
// TYPES
// ============================================

export interface Notification {
  id: string;
  /**
   * VSNP-684: row provenance. Defaults to 'alert' for back-compat
   * (existing rows from the notifications table). 'config_sync' rows
   * are synthesised at read time from edge config state — no
   * mark-read / delete actions on those rows.
   */
  source?: 'alert' | 'config_sync';
  type: 'success' | 'warning' | 'error' | 'info';
  title: string;
  message: string;
  category: string | null;
  read: number; // 0 = unread, 1 = read
  user_id: string | null;
  tenant_id: string | null;
  edge_id: string | null;
  alert_fingerprint: string | null;
  resolved: number; // 0 = active, 1 = resolved
  resolved_at: string | null;
  created_at: string;
  metadata: Record<string, any> | null;
}

export interface Alert {
  annotations: Record<string, string>;
  endsAt: string;
  fingerprint: string;
  receivers: Array<{ name: string }>;
  startsAt: string;
  status: {
    inhibitedBy: string[];
    silencedBy: string[];
    state: 'unprocessed' | 'active' | 'suppressed';
  };
  updatedAt: string;
  generatorURL: string;
  labels: Record<string, string>;
}

export interface AlertGroup {
  labels: Record<string, string>;
  receiver: { name: string };
  alerts: Alert[];
}

export interface Silence {
  id: string;
  matchers: Array<{
    name: string;
    value: string;
    isRegex: boolean;
    isEqual: boolean;
  }>;
  startsAt: string;
  endsAt: string;
  createdBy: string;
  comment: string;
  status: {
    state: 'expired' | 'active' | 'pending';
  };
}

export interface AlertRule {
  id: string;
  file: string;
  group: string;
  alert: string;
  expr: string;
  duration: string;
  severity: string;
  labels: Record<string, string>;
  annotations: Record<string, string>;
  enabled: boolean;
  managed: boolean; // true = system-managed (read-only), false = user-created (editable)
  editable: boolean; // can be edited/deleted via API
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CreateAlertRuleData {
  alert: string;
  expr: string;
  duration?: string;
  severity?: string;
  groupName?: string;
  labels?: Record<string, string>;
  annotations?: Record<string, string>;
}

export interface UpdateAlertRuleData {
  expr?: string;
  duration?: string;
  severity?: string;
  labels?: Record<string, string>;
  annotations?: Record<string, string>;
  enabled?: boolean;
}

export interface CreateSilenceData {
  matchers: Array<{
    name: string;
    value: string;
    isRegex?: boolean;
  }>;
  startsAt: string;
  endsAt: string;
  createdBy: string;
  comment: string;
}

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Notifications API
 */
const getNotifications = async (params?: { limit?: number; unread?: boolean; edge_id?: string }): Promise<Notification[]> => {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.unread) queryParams.append('unread', 'true');
  if (params?.edge_id) queryParams.append('edge_id', params.edge_id);

  const response = await api.get(`/notifications?${queryParams.toString()}`);
  return response as unknown as Notification[];
};

const getUnreadCount = async (): Promise<number> => {
  // Background poll from the header — never trigger the loading bar
  const response = await api.get('/notifications?unread=true&count=true', {
    showLoading: false,
  } as RequestOptions);
  return (response as any)?.count || 0;
};

const markNotificationAsRead = async (id: string): Promise<void> => {
  await api.post(`/notifications/${id}/read`);
};

const deleteNotification = async (id: string): Promise<void> => {
  await api.delete(`/notifications/${id}`);
};

/**
 * Alerts API
 */
const getAlerts = async (options?: RequestOptions): Promise<Alert[]> => {
  const response = await api.get('/alerts', options);
  return response as unknown as Alert[];
};

const getAlertGroups = async (): Promise<AlertGroup[]> => {
  const response = await api.get('/alerts/groups');
  return response as unknown as AlertGroup[];
};

/**
 * Silences API
 */
const getSilences = async (): Promise<Silence[]> => {
  const response = await api.get('/silences');
  return response as unknown as Silence[];
};

const createSilence = async (data: CreateSilenceData): Promise<Silence> => {
  const response = await api.post('/silences', data);
  return response as unknown as Silence;
};

const deleteSilence = async (id: string): Promise<void> => {
  await api.delete(`/silences/${id}`);
};

/**
 * Alert Rules API
 *
 * The backend returns `{ rules, readOnly, readOnlyMessage }` now — read-only
 * mode is true in Kubernetes where /etc/alerts is a read-only ConfigMap
 * mount. Legacy shape (bare array) still supported for local-dev fallback.
 */
export interface AlertRulesResponse {
  rules: AlertRule[];
  readOnly: boolean;
  readOnlyMessage: string | null;
}

const getAlertRules = async (): Promise<AlertRulesResponse> => {
  const response: any = await api.get('/alert-rules');
  // Legacy shape: bare array
  if (Array.isArray(response)) {
    return { rules: response, readOnly: false, readOnlyMessage: null };
  }
  return {
    rules: response.rules ?? [],
    readOnly: response.readOnly ?? false,
    readOnlyMessage: response.readOnlyMessage ?? null,
  };
};

const createAlertRule = async (data: CreateAlertRuleData): Promise<{ id: string; status: string }> => {
  const response = await api.post('/alert-rules', data);
  return response as unknown as { id: string; status: string };
};

const updateAlertRule = async (id: string, data: UpdateAlertRuleData): Promise<{ status: string }> => {
  const response = await api.put(`/alert-rules/${id}`, data);
  return response as unknown as { status: string };
};

const deleteAlertRule = async (id: string): Promise<{ status: string }> => {
  const response = await api.delete(`/alert-rules/${id}`);
  return response as unknown as { status: string };
};

// ============================================
// EXPORT SERVICE
// ============================================

const alertsService = {
  // Notifications
  getNotifications,
  getUnreadCount,
  markNotificationAsRead,
  deleteNotification,

  // Alerts
  getAlerts,
  getAlertGroups,

  // Silences
  getSilences,
  createSilence,
  deleteSilence,

  // Alert Rules
  getAlertRules,
  createAlertRule,
  updateAlertRule,
  deleteAlertRule,
};

export default alertsService;
